import { useEffect, useState } from 'react';

export interface MediaConfig {
  r2PublicBaseUrl: string;
  ipfsEnabled: boolean;
  ipfsGateways: string[];
  videoCacheEnabled: boolean;
}
export interface MediaReference { cid: string; path: string; }
const CID = '(?:Qm[1-9A-HJ-NP-Za-km-z]{44}|b[a-z2-7]{20,120})';
const backend = (process.env.DEV_MODE === 'true' ? process.env.SPEEM_BACKEND_DEV : process.env.SPEEM_BACKEND) || '';
function root(value: unknown): string {
  try {
    const url = new URL(String(value));
    const localHttp = process.env.DEV_MODE === 'true' && url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if ((!localHttp && url.protocol !== 'https:') || url.username || url.password || url.search || url.hash
        || /(?:^|\.)(?:mypinata|pinata)\.cloud$/i.test(url.hostname)) return '';
    return url.toString().replace(/\/$/, '').replace(/\/ipfs$/, '');
  } catch { return ''; }
}
let config: MediaConfig = {
  r2PublicBaseUrl: root(process.env.R2_PUBLIC_BASE_URL),
  ipfsEnabled: false,
  ipfsGateways: [], // Optional routes are advertised by the backend after operator verification.
  videoCacheEnabled: false, // Only the backend may advertise that its cache is enabled.
};
const listeners = new Set<() => void>();
let configRequest: Promise<void> | undefined;
export function setMediaConfig(value: MediaConfig): void {
  const r2 = root(value.r2PublicBaseUrl);
  if (!r2) throw Error('R2 media origin is not configured.');
  config = { r2PublicBaseUrl: r2, ipfsEnabled: value.ipfsEnabled === true,
    ipfsGateways: Array.isArray(value.ipfsGateways) ? value.ipfsGateways.map(root).filter(Boolean) : [],
    videoCacheEnabled: value.videoCacheEnabled === true };
  listeners.forEach(notify => notify());
}
export function loadMediaConfig(): Promise<void> {
  if (!configRequest && backend) {
    configRequest = fetch(`${backend.replace(/\/$/, '')}/api/media/config`, { cache: 'no-store' })
      .then(response => { if (!response.ok) throw Error('Media configuration unavailable'); return response.json(); })
      .then(setMediaConfig).catch(() => { configRequest = undefined; });
  }
  return configRequest || Promise.resolve();
}
export function useMediaConfig(): MediaConfig {
  const [current, setCurrent] = useState(config);
  useEffect(() => {
    const notify = () => setCurrent(config);
    listeners.add(notify); loadMediaConfig();
    return () => { listeners.delete(notify); };
  }, []);
  return current;
}
export function mediaGateways(): string[] {
  return [config.r2PublicBaseUrl, ...(config.ipfsEnabled ? config.ipfsGateways : [])].filter(Boolean);
}
export function preferredGateway(saved?: string): string {
  const allowed = mediaGateways();
  return allowed.includes(saved || '') ? saved as string : config.r2PublicBaseUrl;
}
export function gatewayLabel(value: string): string {
  return value === config.r2PublicBaseUrl ? 'R2' : `IPFS · ${value.replace('https://', '')}`;
}
export function parseMediaReference(input: unknown): MediaReference | null {
  if (typeof input !== 'string' || input.length > 8192) return null;
  const text = input.trim().replace(/[)\]}>.,;!"']+$/, '');
  // Parse gateway paths without ever fetching or trusting their original hostname.
  const match = new RegExp(`(?:^ipfs://(?:ipfs/)?|/ipfs/|^)(${CID})(?=/|[?#]|$)([^?#\\s<>"']*)`).exec(text);
  if (!match) return null;
  try {
    const segments = match[2].split('/').filter(Boolean).map(segment => decodeURIComponent(segment));
    if (segments.some(segment => segment === '.' || segment === '..' || /[\\/\x00-\x1f\x7f]/.test(segment))) return null;
    return { cid: match[1], path: segments.length ? '/' + segments.map(encodeURIComponent).join('/') : '' };
  } catch { return null; }
}
export function r2MediaUrl(input: unknown): string {
  const ref = parseMediaReference(input);
  return ref && config.r2PublicBaseUrl ? `${config.r2PublicBaseUrl}/ipfs/${ref.cid}${ref.path}` : '';
}
export function mediaUrl(input: unknown, gateway?: string, allowCache = true): string {
  const ref = parseMediaReference(input);
  if (!ref) return '';
  const selected = preferredGateway(gateway);
  if (!selected) return '';
  if (allowCache && selected === config.r2PublicBaseUrl && config.videoCacheEnabled && backend && !ref.path) {
    return `${backend.replace(/\/$/, '')}/stream/${ref.cid}`;
  }
  return `${selected}/ipfs/${ref.cid}${ref.path}`;
}
export function extractVideoReference(body: unknown, rawMetadata?: unknown): string | null {
  let metadata: any = rawMetadata || {};
  if (typeof metadata === 'string') { try { metadata = JSON.parse(metadata); } catch { metadata = {}; } }
  const video = metadata?.video;
  const candidates: unknown[] = [];
  if (typeof body === 'string') {
    // Historical optimization rewrote the body first. Exclude thumbnail/image syntax.
    const clean = body.replace(/!\[[^\]]*\]\([^)]*\)/g, '').replace(/<img\b[^>]*>/gi, '');
    const labelled = /\[(?:IPFS Video|Watch(?: Video)?)\]\(([^)]+)\)/gi;
    let match: RegExpExecArray | null;
    while ((match = labelled.exec(clean))) candidates.push(match[1]);
    const links = clean.match(/(?:https?:\/\/[^\s<>"']+\/ipfs\/|ipfs:\/\/)[^\s<>"'\[\]()*|]+/gi) || [];
    candidates.push(...links.filter(link => !/\.(?:png|jpe?g|webp|gif)(?:[?#)]|$)/i.test(link)));
  }
  const bodyCandidates = candidates.length;
  const videoTagged = Array.isArray(metadata?.tags) && metadata.tags.some((tag: unknown) => ['steemvideo', 'videosteem', 'speem'].includes(String(tag).toLowerCase()));
  if (typeof video === 'string') candidates.push(video);
  else if (video && typeof video === 'object') {
    candidates.push(video.content?.url, video.url, video.files?.ipfs?.vid?.src,
      video.files?.ipfs?.video, video.files?.ipfs?.src, video.files?.ipfs);
  }
  if (Array.isArray(metadata?.links)) candidates.push(...metadata.links.filter((link: unknown) => typeof link === 'string' && !/\.(?:png|jpe?g|webp|gif)(?:[?#)]|$)/i.test(link)));
  const imageRefs = [metadata?.thumbnail, metadata?.cover, ...(Array.isArray(metadata?.image) ? metadata.image : [])]
    .map(parseMediaReference).filter(Boolean);
  for (const [index, candidate] of candidates.entries()) {
    const ref = parseMediaReference(candidate);
    if (ref && ((index < bodyCandidates && videoTagged) || !imageRefs.some(image => image?.cid === ref.cid && image?.path === ref.path))) return `ipfs://${ref.cid}${ref.path}`;
  }
  return null;
}

// New published references always use the public R2 URL, never the optional VPS cache.
export function normalizePostMedia<T extends { link?: string; body?: string }>(post: T): T {
  const ref = parseMediaReference(post.link);
  if (!ref) return post;
  const link = r2MediaUrl(post.link);
  if (!link) throw Error('R2 media origin is not configured.');
  const body = typeof post.body === 'string' && post.link ? post.body.split(post.link).join(link) : post.body;
  return { ...post, link, body };
}

export async function ipfsAvailability(cid: string, signal?: AbortSignal): Promise<boolean> {
  if (!config.ipfsEnabled || !parseMediaReference(cid)) return false;
  try {
    const response = await fetch(`${backend}/api/media/availability/${encodeURIComponent(cid)}`, { signal });
    return response.ok && (await response.json()).ipfsReady === true;
  } catch { return false; }
}
